/**
 * Phase 25 — Per-section dirty tracking.
 *
 * Covers: DIRTY25-01 (computeDirtySections), DIRTY25-02 (snapshot reset on Save).
 *
 * Implementation: compare the tracked field set per section via JSON.stringify
 * deep-equal of the section sub-object. Hydration sets the initial snapshot
 * AFTER server data arrives (Pitfall 4 — avoid lit-on-load).
 *
 * React state updates rebuild section objects via spread, so object key order
 * is stable across renders — making JSON.stringify a safe diff oracle here.
 *
 * Phase 26 (UI26-04) — Pricing section normalization step:
 *   - Strips `showArchived` (UI-only display flag).
 *   - Forces every periodOverride row's `status` to 'Active' before stringify
 *     so flipping Active<->Archived produces identical diffs (operator
 *     archiving / reactivating a row counts as a state change saved via the
 *     savePricing archive branch, not as a Pricing "dirty" indicator).
 *   - Phase 25 DIRTY25-01 regression preserved: editing any other field
 *     (discountPct, price, validityDays, securityDepositAmount, taxRatePct,
 *     etc.) still flips Pricing dirty.
 *
 * Phase 27 invariants (PRICE27-* / SEATS27 UI):
 *   - `seats` is part of every row and DOES count toward Pricing dirty
 *     (changing seats reshapes the matrix row and must trigger a save).
 *   - `status` Active↔Archived flip still does NOT count as Pricing dirty
 *     (Phase 26 UI26-04 contract preserved).
 *   - `isLegacyAbsolute` is a hydration-only display flag — stripped before
 *     stringify so its presence/absence never lights Pricing dirty by itself.
 */

import type { PlanEditorState, PlanEditorSection, PricingState } from './types';

const SECTIONS: PlanEditorSection[] = ['basics', 'pricing', 'amenities', 'credits', 'access'];

/**
 * Phase 26 UI26-04 — normalize PricingState for dirty comparison.
 *
 * - Drops `showArchived` (UI-only).
 * - Coerces every periodOverride status to 'Active' so archive-toggle
 *   round-trips do not count as dirty.
 */
function normalizePricingForDirty(p: PricingState): unknown {
  return {
    securityDepositRequired: p.securityDepositRequired,
    securityDepositAmount: p.securityDepositAmount,
    taxRatePct: p.taxRatePct,
    pricingSaved: p.pricingSaved,
    periodOverrides: p.periodOverrides.map((o) => {
      // Strip isLegacyAbsolute (hydration-only display flag); coerce status
      // to 'Active' (archive flip handled by savePricing, not "dirty"). seats
      // is intentionally INCLUDED so changing it counts toward Pricing dirty.
      const { isLegacyAbsolute: _legacy, ...rest } = o;
      return {
        ...rest,
        status: 'Active' as const,
      };
    }),
    // showArchived intentionally stripped.
  };
}

export function computeDirtySections(
  initial: PlanEditorState,
  current: PlanEditorState,
): Set<PlanEditorSection> {
  const dirty = new Set<PlanEditorSection>();
  for (const key of SECTIONS) {
    const a = key === 'pricing' ? normalizePricingForDirty(initial.pricing) : initial[key];
    const b = key === 'pricing' ? normalizePricingForDirty(current.pricing) : current[key];
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      dirty.add(key);
    }
  }
  return dirty;
}
